import React from 'react'
import styled from 'styled-components'
import { Formik, FormikProps } from 'formik'
import * as yup from 'yup'

import Checkbox from 'components/Checkbox'
import Form from 'components/Form'
import Input from 'components/Input'
import PasswordInput from 'components/PasswordInput'
import Button from 'components/Button'
import Link from 'components/Link'

import { trackEvent } from 'modules/analytics'

import {
  createPersonalAuthorization,
  setUserAuthorization,
} from 'graphql/multisession'
import {
  useSignUpMutation,
  GetInitialUserDocument,
  GetInitialUserQuery,
} from 'gqlTypes'

import { useTrackEditedFields } from './useTrackEditedFields.old'
import {
  SIGN_UP_INITIAL_VALUES,
  TERMS_OF_SERVICE_PRIVACY_POLICY_ERROR,
} from './constants'
import { ParsedError } from 'graphql/apollo/useMutation'

const signUpSchema = yup.object().shape({
  email: yup
    .string()
    .email('This is not a valid email')
    .required('Enter your email address'),
  name: yup.string().trim().required('Enter your name'),
  password: yup
    .string()
    .min(8, 'Should be 8 or more characters')
    .max(72, 'Should be 72 or less characters')
    .required('Enter a new password'),
  'terms-of-service': yup.boolean(),
  'privacy-policy': yup.boolean(),
})

type Values = typeof SIGN_UP_INITIAL_VALUES

interface ExtendedFormikProps extends FormikProps<Values> {
  apiError: ParsedError | undefined
}

const StyledCheckbox = styled(Checkbox)`
  /*
  Prevent the TOS link from being "blocked" by the input.
  */
  input {
    width: auto;
  }
`

type FieldsProps = FormikProps<Values> & { isInShareInviteContext?: boolean }
export const Fields: React.FC<FieldsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  /**
   * Fields can be rendered as part of the main sign up form
   * but also as part of a sign up form when invited to a share.
   */
  isInShareInviteContext = false,
  ...others
}) => {
  useTrackEditedFields(values, isInShareInviteContext)
  const formContext = isInShareInviteContext ? 'share invite' : 'main'

  return (
    <>
      <Form.Field
        name="name"
        label="Name"
        errorText={touched.name ? errors.name : undefined}>
        <Input
          type="text"
          name="name"
          placeholder="John Appleseed"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          autoFocus
        />
      </Form.Field>
      <Form.Field
        name="email"
        label="Email"
        errorText={touched.email ? errors.email : undefined}>
        <Input
          name="email"
          type="email"
          placeholder="john@appleseed.com"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Form.Field>
      <Form.Field
        label="Password"
        name="password"
        errorText={touched.password ? errors.password : undefined}>
        <PasswordInput
          name="password"
          placeholder="••••••••"
          value={values.password}
          onChange={handleChange}
          onBlur={handleBlur}
          strengthMeter
        />
      </Form.Field>
      <Form.Field name="terms-of-service" mt={2} mb={3}>
        <StyledCheckbox
          name="terms-of-service"
          checked={Boolean(values['terms-of-service'])}
          onChange={handleChange}
          onBlur={handleBlur}>
          I have read and agree to the Sketch&nbsp;
          <Link
            variant="secondary"
            isUnderlined
            external
            href="https://www.sketch.com/tos"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent('SIGN UP - "Terms of Service" link opened', {
                formContext,
              })
            }}>
            Terms of Service
          </Link>
          .
        </StyledCheckbox>
      </Form.Field>
      <Form.Field name="privacy-policy" mb={8}>
        <StyledCheckbox
          name="privacy-policy"
          checked={Boolean(values['privacy-policy'])}
          onChange={handleChange}
          onBlur={handleBlur}>
          I have read and agree to the Sketch&nbsp;
          <Link
            variant="secondary"
            isUnderlined
            external
            href="https://www.sketch.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent('SIGN UP - "Privacy Policy" link opened', {
                formContext,
              })
            }}>
            Privacy Policy
          </Link>
          .
        </StyledCheckbox>
      </Form.Field>
    </>
  )
}

export const Errors: React.FC<ExtendedFormikProps> = ({ apiError, status }) =>
  status || apiError ? (
    <Form.ErrorField>{status || apiError?.message || ''}</Form.ErrorField>
  ) : null

export const DefaultFormFields: React.FC<ExtendedFormikProps> = formProps => (
  <>
    <Fields {...formProps} />
    <Errors {...formProps} />
    <Submit
      isSubmitting={formProps.isSubmitting}
      onClick={() => {
        const submitCount = formProps.submitCount + 1
        trackEvent('SIGN UP - Sign up form submitted', {
          submitCount: submitCount,
          formContext: 'main',
        })
      }}
    />
  </>
)

interface SubmitProps {
  isSubmitting: boolean
  onClick?: () => void
}

export const Submit: React.FC<SubmitProps> = ({ isSubmitting, onClick }) => (
  <Form.Footer>
    <Button
      variant="primary-untinted"
      type="submit"
      fill
      small
      disabled={isSubmitting}
      loading={isSubmitting}
      onClick={onClick}>
      Sign Up
    </Button>
  </Form.Footer>
)

export interface SignUpFormProps {
  email?: string
  children?: (formikProps: ExtendedFormikProps) => React.ReactNode
  onCompleted?: () => void
  onError?: (message: string) => void
}

const SignUpForm: React.FC<SignUpFormProps> = ({
  children = DefaultFormFields,
  email = '',
  onCompleted,
  onError,
}) => {
  const [signUp, { loading, error: apiError }] = useSignUpMutation({
    redirectErrors: true,
    update: (cache, { data }) => {
      if (!data?.signUp) {
        return
      }

      /* Save the user credentials in the apollo cache */
      setUserAuthorization(
        cache,
        createPersonalAuthorization(data.signUp!.credentials!)
      )

      /* Save the user profile information */
      cache.writeQuery<GetInitialUserQuery>({
        query: GetInitialUserDocument,
        data: {
          __typename: 'RootQueryType',
          me: data.signUp.user!,
        },
      })
    },
    onCompleted: () => {
      onCompleted?.()
    },
    onError: ({ message }) => {
      onError?.(message)
    },
  })

  return (
    <Formik
      initialValues={{ ...SIGN_UP_INITIAL_VALUES, email }}
      onSubmit={async (values, actions) => {
        try {
          if (!values['terms-of-service'] || !values['privacy-policy']) {
            actions.setStatus(TERMS_OF_SERVICE_PRIVACY_POLICY_ERROR)
            return
          }

          actions.setStatus()

          const {
            'privacy-policy': privacyPolicyAgreed,
            'terms-of-service': tosAgreed,
            ...otherValues
          } = values

          await signUp({
            variables: {
              input: {
                ...otherValues,
                privacyPolicyAgreed,
                tosAgreed,
              },
            },
          })
        } catch (e) {
          // The error handling should be taken care of by Apollo
        } finally {
          actions.setSubmitting(false)
        }
      }}
      validationSchema={signUpSchema}
      validateOnBlur={false}
      enableReinitialize>
      {formikbag => (
        <Form>
          {children({ ...formikbag, isSubmitting: loading, apiError })}
        </Form>
      )}
    </Formik>
  )
}

export default SignUpForm
